/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Routes, Route, useNavigate, Link, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Mail, Lock, User, Phone, MapPin, ArrowRight, LogOut, Home,
  Wallet, Users, DollarSign, Share2, Copy, TrendingUp, Loader2, ChevronRight,
  Globe, CheckCircle2, Clock, Settings,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TOK = "brivo_ref_token";

function rapi() {
  const i = axios.create({ baseURL: API });
  i.interceptors.request.use((c) => {
    const t = localStorage.getItem(TOK);
    if (t) c.headers.Authorization = `Bearer ${t}`;
    return c;
  });
  return i;
}

export default function ReferralApp() {
  return (
    <Routes>
      <Route index element={<RefLanding />} />
      <Route path="landing" element={<RefLanding />} />
      <Route path="register" element={<RefRegister />} />
      <Route path="login" element={<RefLogin />} />
      <Route path="forgot" element={<RefForgot />} />
      <Route path="*" element={<RefDashboardShell />} />
    </Routes>
  );
}

// ──────────────────── LANDING ────────────────────
function RefLanding() {
  return (
    <div className="min-h-screen bg-[#FFFCF2] text-slate-900 font-sans" data-testid="ref-landing">
      <header className="px-6 lg:px-12 py-5 flex items-center justify-between bg-white/80 border-b border-black/5 sticky top-0 backdrop-blur-md z-50">
        <Link to="/" className="font-display font-black text-2xl tracking-tight text-black">Brivo360</Link>
        <div className="flex items-center gap-4">
          <Link to="/r/login" className="text-sm font-bold text-black/60 hover:text-black transition-colors" data-testid="ref-login-link">Login</Link>
          <Link to="/r/register" className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#2D6A4F] hover:opacity-95 text-white text-sm font-bold transition-all shadow-sm" data-testid="ref-register-link">Start Earning</Link>
        </div>
      </header>

      <section className="px-6 lg:px-10 py-20 lg:py-28 max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-3.5 py-1.5 rounded-full text-xs font-bold mb-6 shadow-sm">
          <Sparkles size={13} className="text-emerald-600" /> Brivo360 Referral Program
        </div>
        <h1 className="font-display font-black text-5xl lg:text-7xl text-black leading-[1.1] tracking-tight">
          Earn while businesses<br/>grow with Brivo360.
        </h1>
        <p className="mt-6 text-lg text-black/65 max-w-2xl mx-auto leading-relaxed">
          Refer businesses to Brivo360 and earn recurring commissions on every payment. No code, no fees, no caps.
        </p>
        
        <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/r/register" className="px-8 py-4 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#2D6A4F] hover:opacity-95 text-white font-bold inline-flex items-center gap-2 transition-all shadow-md shadow-emerald-800/10">
            Start earning <ArrowRight size={16} />
          </Link>
          <Link to="/r/login" className="px-8 py-4 rounded-xl bg-white border border-black/10 text-black font-bold hover:bg-black/[0.02] transition-all shadow-sm">
            Login to dashboard
          </Link>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Users, t: "Refer", d: "Share your unique referral code with businesses you know." },
            { icon: TrendingUp, t: "Track", d: "Watch your earnings grow in real-time on your dashboard." },
            { icon: Wallet, t: "Cashout", d: "Withdraw to your bank as soon as you hit ₦5,000." },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border border-black/5 p-7 text-left shadow-sm hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-[#2D6A4F] flex items-center justify-center mb-4 border border-emerald-100"><s.icon size={20} /></div>
              <div className="font-display font-extrabold text-lg text-black tracking-tight">{s.t}</div>
              <p className="text-sm text-black/60 mt-1.5 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ──────────────────── AUTH ────────────────────
function AuthShell({ title, sub, children, testid }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-12 bg-[#FFFCF2]" data-testid={testid}>
      <div className="hidden lg:flex lg:col-span-5 bg-gradient-to-br from-[#2D6A4F] via-[#1FA868] to-emerald-900 p-12 flex-col justify-between text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/10 blur-3xl -mr-20 -mt-20" />
        <Link to="/" className="font-display font-black text-2xl tracking-tight">Brivo360</Link>
        <div>
          <h2 className="font-display font-extrabold text-4xl leading-tight tracking-tight">Earn from businesses you refer to Brivo360.</h2>
          <p className="mt-4 text-white/80 text-sm leading-relaxed">Track every referral, every commission, every payout — in one elegant dashboard.</p>
        </div>
        <div className="text-xs font-bold uppercase tracking-wider opacity-60">Brivo360 Referral Program</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 lg:col-span-7">
        <div className="w-full max-w-md bg-white p-8 sm:p-10 rounded-2xl border border-black/5 shadow-sm">
          <h1 className="font-display font-extrabold text-3xl text-black tracking-tight">{title}</h1>
          <p className="text-sm text-black/55 mt-1">{sub}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

function RefRegister() {
  const nav = useNavigate();
  const params = new URLSearchParams(useLocation().search);
  const [f, setF] = useState({
    full_name: "", email: "", phone: "", password: "", confirm: "",
    country: "Nigeria", state: "", city: "", address: "",
    upline_code: params.get("ref") || "",
  });
  const [loading, setLoading] = useState(false);
  const [upline, setUpline] = useState(null);

  useEffect(() => {
    if (f.upline_code) {
      axios.get(`${API}/referral/validate/${f.upline_code}`).then((r) => setUpline(r.data?.valid ? r.data : null)).catch(() => {});
    }
  }, [f.upline_code]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return toast.error("Passwords don't match");
    if (f.password.length < 8) return toast.error("Password must be 8+ chars");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/referral/auth/register`, { ...f, upline_code: f.upline_code || null });
      localStorage.setItem(TOK, data.token);
      toast.success(`Welcome ${data.user.full_name}!`);
      nav("/r/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Join the program" sub="Create your referral partner account" testid="ref-register-page">
      <form onSubmit={submit} className="mt-6 space-y-3.5">
        <AInput icon={User} placeholder="Full name *" value={f.full_name} onChange={(v) => setF({ ...f, full_name: v })} testid="ref-fullname" />
        <AInput icon={Mail} type="email" placeholder="Email *" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="ref-email" />
        <AInput icon={Phone} placeholder="Phone *" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="ref-phone" />
        
        <div className="grid grid-cols-3 gap-2 bg-slate-50 p-2 rounded-xl border border-black/5">
          <AInput placeholder="Country" value={f.country} onChange={(v) => setF({ ...f, country: v })} testid="ref-country" />
          <AInput placeholder="State" value={f.state} onChange={(v) => setF({ ...f, state: v })} testid="ref-state" />
          <AInput placeholder="City" value={f.city} onChange={(v) => setF({ ...f, city: v })} testid="ref-city" />
        </div>
        
        <AInput icon={Lock} type="password" placeholder="Password (8+ chars) *" value={f.password} onChange={(v) => setF({ ...f, password: v })} testid="ref-password" />
        <AInput icon={Lock} type="password" placeholder="Confirm password *" value={f.confirm} onChange={(v) => setF({ ...f, confirm: v })} testid="ref-confirm-password" />
        <AInput placeholder="Upline referral code (optional)" value={f.upline_code} onChange={(v) => setF({ ...f, upline_code: v })} testid="ref-upline-code" />
        
        {upline?.valid && (
          <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 px-3 py-2.5 rounded-xl flex items-center gap-1.5">
            <CheckCircle2 size={14} className="text-emerald-600" /> Referred by <strong>{upline.name}</strong>
          </div>
        )}
        
        <button type="submit" disabled={loading} className="w-full py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold transition-colors disabled:opacity-50 shadow-sm flex items-center justify-center gap-2" data-testid="ref-register-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Create account"}
        </button>
        <p className="text-sm text-center text-black/55">Already have one? <Link to="/r/login" className="text-emerald-700 font-bold hover:underline">Login</Link></p>
      </form>
    </AuthShell>
  );
}

function RefLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/referral/auth/login`, { email, password });
      localStorage.setItem(TOK, data.token);
      toast.success("Welcome back");
      nav("/r/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Welcome back" sub="Log in to your referral account" testid="ref-login-page">
      <form onSubmit={submit} className="mt-6 space-y-3.5">
        <AInput icon={Mail} type="email" placeholder="Email" value={email} onChange={setEmail} testid="ref-login-email" />
        <AInput icon={Lock} type="password" placeholder="Password" value={password} onChange={setPassword} testid="ref-login-password" />
        
        <button type="submit" disabled={loading} className="w-full py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold transition-colors disabled:opacity-50 shadow-sm flex items-center justify-center gap-2" data-testid="ref-login-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Log in"}
        </button>
        
        <div className="text-sm text-center flex items-center justify-between pt-1">
          <Link to="/r/forgot" className="text-[#2D6A4F] font-bold hover:underline">Forgot password?</Link>
          <Link to="/r/register" className="text-[#2D6A4F] font-bold hover:underline">Sign up</Link>
        </div>
      </form>
    </AuthShell>
  );
}

function RefForgot() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState(1);

  const sendOtp = async () => { 
    try { 
      await axios.post(`${API}/referral/auth/forgot`, { email }); 
      toast.success("OTP sent (if your email exists)"); 
      setStep(2); 
    } catch { 
      toast.error("Failed"); 
    } 
  };

  const reset = async () => { 
    try { 
      await axios.post(`${API}/referral/auth/reset`, { email, otp, new_password: newPassword }); 
      toast.success("Password reset — please login"); 
      window.location.href = "/r/login"; 
    } catch (e) { 
      toast.error(e?.response?.data?.detail || "Failed"); 
    } 
  };

  return (
    <AuthShell title="Reset password" sub="We'll email you a one-time code">
      <div className="mt-6 space-y-3.5">
        <AInput icon={Mail} type="email" placeholder="Your email" value={email} onChange={setEmail} disabled={step === 2} testid="ref-forgot-email" />
        {step === 1 ? (
          <button onClick={sendOtp} className="w-full py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold shadow-sm transition-colors" data-testid="ref-forgot-send-otp">Send OTP</button>
        ) : (
          <>
            <AInput placeholder="6-digit OTP" value={otp} onChange={setOtp} testid="ref-forgot-otp" />
            <AInput icon={Lock} type="password" placeholder="New password" value={newPassword} onChange={setNewPassword} testid="ref-forgot-new-password" />
            <button onClick={reset} className="w-full py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold shadow-sm transition-colors" data-testid="ref-forgot-reset">Reset password</button>
          </>
        )}
        <p className="text-sm text-center pt-1"><Link to="/r/login" className="text-[#2D6A4F] font-bold hover:underline">Back to login</Link></p>
      </div>
    </AuthShell>
  );
}

function AInput({ icon: Icon, testid, ...props }) {
  return (
    <div className="relative w-full">
      {Icon && <Icon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-black/35" />}
      <input 
        {...props} 
        data-testid={testid} 
        onChange={(e) => props.onChange(e.target.value)}
        className={`w-full ${Icon ? "pl-11" : "pl-4"} pr-4 py-3 rounded-xl border border-black/10 bg-white text-sm text-slate-900 placeholder-black/30 focus:border-emerald-600 focus:outline-none focus:ring-4 focus:ring-emerald-600/5 transition-all disabled:bg-slate-50 disabled:text-slate-400`} 
      />
    </div>
  );
}

// ──────────────────── DASHBOARD SHELL ────────────────────
function RefDashboardShell() {
  const nav = useNavigate();
  const loc = useLocation();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    rapi().get("/referral/me").then((r) => { setMe(r.data); setLoading(false); })
      .catch(() => { localStorage.removeItem(TOK); nav("/r/login"); });
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-[#FFFCF2] flex items-center justify-center">
      <Loader2 className="animate-spin text-emerald-600" size={24} />
    </div>
  );
  if (!me) return <Navigate to="/r/login" replace />;

  const sub = loc.pathname.split("/").filter(Boolean)[1] || "dashboard";
  const logout = () => { localStorage.removeItem(TOK); nav("/r/login"); };

  return (
    <div className="min-h-screen bg-[#FFFCF2] flex text-slate-900" data-testid="ref-portal">
      <aside className="w-64 bg-slate-900 border-r border-slate-800 p-5 hidden lg:flex flex-col text-slate-300 justify-between">
        <div className="space-y-6">
          <Link to="/" className="font-display font-black text-xl text-white tracking-tight flex items-center gap-2 px-1">
            Brivo360 Referrals
          </Link>
          <nav className="space-y-1">
            {[
              { k: "dashboard", lbl: "Dashboard", icon: Home },
              { k: "referrals", lbl: "My Referrals", icon: Users },
              { k: "wallet", lbl: "Wallet", icon: Wallet },
              { k: "payouts", lbl: "Payouts", icon: DollarSign },
              { k: "share", lbl: "Share & Earn", icon: Share2 },
              { k: "profile", lbl: "Profile", icon: Settings },
            ].map((t) => {
              const Icon = t.icon, active = sub === t.k;
              return (
                <Link key={t.k} to={`/r/${t.k}`} data-testid={`ref-nav-${t.k}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${active ? "bg-emerald-600 text-white shadow-sm shadow-emerald-700/10" : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}>
                  <Icon size={16} /> {t.lbl}
                </Link>
              );
            })}
          </nav>
        </div>
        
        <div className="bg-slate-800/40 border border-slate-800 rounded-xl p-4 space-y-3">
          <div className="min-w-0">
            <div className="font-bold text-sm text-white truncate">{me.full_name}</div>
            <div className="text-xs text-slate-400 truncate mt-0.5">{me.email}</div>
          </div>
          <div className="inline-flex items-center justify-between gap-1 w-full bg-slate-950 text-emerald-400 px-2.5 py-1.5 rounded-lg text-xs font-mono font-bold border border-emerald-500/10">
            <span className="opacity-50 text-[10px]">CODE:</span>
            <span>{me.referral_code}</span>
          </div>
          <button onClick={logout} className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700/40 hover:bg-rose-950/20 text-xs font-bold text-slate-400 hover:text-rose-400 transition-all" data-testid="ref-logout">
            <LogOut size={13} /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto min-w-0">
        <Routes>
          <Route path="/" element={<Navigate to="/r/dashboard" replace />} />
          <Route path="dashboard" element={<RefDashboard me={me} />} />
          <Route path="referrals" element={<RefReferrals />} />
          <Route path="wallet" element={<RefWallet me={me} setMe={setMe} />} />
          <Route path="payouts" element={<RefPayouts me={me} />} />
          <Route path="share" element={<RefShare me={me} />} />
          <Route path="profile" element={<RefProfile me={me} setMe={setMe} />} />
          <Route path="*" element={<Navigate to="/r/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}

const ngn = (n) => `₦${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function RefDashboard({ me }) {
  const [d, setD] = useState(null);
  useEffect(() => { rapi().get("/referral/dashboard").then((r) => setD(r.data)); }, []);
  
  if (!d) return <div className="p-8"><Loader2 className="animate-spin text-emerald-600" /></div>;
  const k = d.kpis || {};
  
  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="ref-dashboard">
      <div>
        <h1 className="font-display font-black text-3xl text-black">Hello {me.full_name?.split(" ")[0]} 👋</h1>
        <p className="text-sm text-black/55 mt-1">Here's your referral performance at a glance</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <RefKPI t="Total earnings" v={ngn(k.total_earnings)} icon={TrendingUp} accent="bg-emerald-50 border-emerald-100 text-emerald-700" testid="ref-kpi-earnings" />
        <RefKPI t="Wallet balance" v={ngn(k.wallet_balance)} icon={Wallet} accent="bg-emerald-50 border-emerald-100 text-emerald-700" testid="ref-kpi-wallet" />
        <RefKPI t="Pending" v={ngn(k.pending_earnings)} icon={Clock} accent="bg-amber-50 border-amber-100 text-amber-700" />
        <RefKPI t="Businesses referred" v={k.businesses_referred || 0} icon={Users} testid="ref-kpi-businesses" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-black/5 p-6 shadow-sm">
          <div className="font-display font-bold text-base text-black mb-4">Earnings (last 6 months)</div>
          {!d.monthly_earnings || Object.keys(d.monthly_earnings).length === 0 ? (
            <div className="text-center py-12 text-black/45 text-sm">No earnings yet — share your code below to start.</div>
          ) : (
            <div className="space-y-3.5">
              {Object.entries(d.monthly_earnings).slice(-6).map(([k, v]) => {
                const max = Math.max(...Object.values(d.monthly_earnings)) || 1;
                return (
                  <div key={k} className="flex items-center gap-4 text-sm font-medium">
                    <span className="w-16 text-black/55 font-mono text-xs">{k}</span>
                    <div className="flex-1 h-2.5 bg-black/5 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-600 rounded-full transition-all duration-500" style={{ width: `${(v / max) * 100}%` }} />
                    </div>
                    <span className="font-bold tabular-nums w-24 text-right text-black">{ngn(v)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 rounded-2xl p-6 text-white flex flex-col justify-between shadow-sm">
          <div>
            <div className="font-bold text-xs uppercase tracking-wider text-slate-400">Your referral code</div>
            <div className="font-display font-black text-3xl mt-3 font-mono tracking-wider text-emerald-400 bg-slate-950 border border-slate-800 p-3 rounded-xl select-all">
              {k.referral_code || me.referral_code}
            </div>
          </div>
          <button 
            onClick={() => { navigator.clipboard.writeText(k.referral_code || me.referral_code); toast.success("Copied"); }}
            className="mt-6 w-full px-4 py-3 rounded-xl bg-white hover:bg-slate-50 text-slate-950 font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-sm"
          >
            <Copy size={13} /> Copy code
          </button>
        </div>
      </div>
    </div>
  );
}

function RefKPI({ t, v, icon: Icon, accent, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="text-[11px] uppercase tracking-wider font-bold text-black/45">{t}</div>
          <div className="font-display font-black text-2xl text-black tabular-nums tracking-tight">{v}</div>
        </div>
        {Icon && (
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${accent || "bg-black/5 border-black/5 text-black/60"}`}>
            <Icon size={18} />
          </div>
        )}
      </div>
    </div>
  );
}

function RefReferrals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { 
    rapi().get("/referral/referrals")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 lg:p-10 space-y-5" data-testid="ref-referrals">
      <h1 className="font-display font-black text-2xl text-black">My Referrals</h1>
      <div className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="animate-spin text-emerald-600 mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center max-w-sm mx-auto">
            <Users size={32} className="mx-auto text-black/20 mb-2" />
            <div className="font-bold text-black/60 text-sm">No referrals yet</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-black/5 text-black/50 font-bold text-[11px] uppercase tracking-wider">
                  <th className="px-6 py-4">Tenant</th>
                  <th className="px-6 py-4">Created At</th>
                  <th className="px-6 py-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5 font-medium text-black/80">
                {items.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs uppercase font-mono">
                          {(r.tenant_name || "?").slice(0, 2)}
                        </div>
                        <div>
                          <div className="font-bold text-black">{r.tenant_name}</div>
                          <div className="text-xs text-black/45 font-mono mt-0.5">{r.tenant_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs font-mono text-black/55">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—"}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${r.status === "active" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-black/5 border-black/5 text-black/50"}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function RefWallet({ me, setMe }) {
  const [d, setD] = useState(null);
  const [bank, setBank] = useState({ bank_name: "", account_name: "", account_number: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { 
    rapi().get("/referral/wallet").then((r) => { 
      setD(r.data); 
      if (r.data?.bank_details) setBank(r.data.bank_details); 
    }); 
  }, []);

  const saveBank = async () => { 
    setSaving(true); 
    try { 
      await rapi().put("/referral/wallet/bank", bank); 
      toast.success("Bank details saved"); 
    } catch (e) { 
      toast.error(e?.response?.data?.detail); 
    } finally { 
      setSaving(false); 
    } 
  };

  if (!d) return <div className="p-8"><Loader2 className="animate-spin text-emerald-600" /></div>;

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-5xl" data-testid="ref-wallet">
      <h1 className="font-display font-black text-2xl text-black">Wallet</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <RefKPI t="Balance" v={ngn(d.wallet_balance)} icon={Wallet} accent="bg-emerald-50 border-emerald-100 text-emerald-700" />
        <RefKPI t="Lifetime earned" v={ngn(d.lifetime_earnings)} icon={TrendingUp} accent="bg-emerald-50 border-emerald-100 text-emerald-700" />
        <RefKPI t="Lifetime paid out" v={ngn(d.lifetime_paid)} icon={DollarSign} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm lg:col-span-5 space-y-4">
          <div className="font-display font-bold text-base text-black">Bank details</div>
          <div className="space-y-3">
            <AInput placeholder="Bank name" value={bank.bank_name} onChange={(v) => setBank({ ...bank, bank_name: v })} testid="ref-bank-name" />
            <AInput placeholder="Account name" value={bank.account_name} onChange={(v) => setBank({ ...bank, account_name: v })} />
            <AInput placeholder="Account number" value={bank.account_number} onChange={(v) => setBank({ ...bank, account_number: v })} testid="ref-bank-number" />
            <button onClick={saveBank} disabled={saving} className="w-full py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold transition-all disabled:opacity-50 shadow-sm" data-testid="ref-bank-save">
              {saving ? "Saving…" : "Save bank"}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm lg:col-span-7">
          <div className="font-display font-bold text-base text-black mb-3">Recent earnings</div>
          <div className="divide-y divide-black/5 max-h-[280px] overflow-y-auto pr-1">
            {d.earnings?.length ? d.earnings.slice(0, 10).map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-4 py-3 text-sm font-medium">
                <div>
                  <div className="font-bold text-black">{e.tenant_name || "—"}</div>
                  <div className="text-xs text-black/40 font-mono mt-0.5">{e.created_at ? new Date(e.created_at).toLocaleString() : "—"}</div>
                </div>
                <div className="text-right space-y-1">
                  <div className="font-bold text-emerald-700 font-mono">+{ngn(e.amount)}</div>
                  <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${e.status === "paid" ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-amber-50 border-amber-100 text-amber-700"}`}>
                    {e.status}
                  </span>
                </div>
              </div>
            )) : (
              <div className="text-sm text-black/45 text-center py-8">No earnings yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RefPayouts({ me }) {
  const [items, setItems] = useState([]);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const load = () => rapi().get("/referral/payouts").then((r) => setItems(r.data || []));
  
  useEffect(() => { load(); }, []);

  const request = async () => {
    if (!amount || amount < 5000) return toast.error("Minimum is ₦5,000");
    setLoading(true);
    try { 
      await rapi().post("/referral/payouts", { amount: parseFloat(amount) }); 
      toast.success("Payout requested"); 
      setAmount(""); 
      load(); 
    } catch (e) { 
      toast.error(e?.response?.data?.detail || "Failed"); 
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 space-y-5 max-w-4xl" data-testid="ref-payouts">
      <h1 className="font-display font-black text-2xl text-black">Payouts</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-start">
        <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm md:col-span-5 space-y-4">
          <div>
            <div className="text-xs uppercase font-bold text-black/45">Wallet balance</div>
            <div className="font-display font-black text-3xl text-black tracking-tight tabular-nums mt-0.5">{ngn(me.wallet_balance)}</div>
          </div>
          
          <div className="space-y-2">
            <div className="flex flex-col sm:flex-row items-stretch gap-2">
              <input 
                type="number" 
                value={amount} 
                onChange={(e) => setAmount(e.target.value)} 
                placeholder="Amount (₦5,000 minimum)" 
                className="flex-1 border border-black/10 rounded-xl px-4 py-2.5 text-sm bg-white font-medium focus:border-emerald-600 focus:outline-none focus:ring-4 focus:ring-emerald-600/5" 
                data-testid="ref-payout-amount" 
              />
              <button onClick={request} disabled={loading} className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-sm transition-colors flex items-center justify-center min-w-[120px]" data-testid="ref-payout-request">
                {loading ? <Loader2 size={16} className="animate-spin" /> : "Request payout"}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-black/5 shadow-sm md:col-span-7 overflow-hidden">
          {items.length === 0 ? (
            <div className="p-12 text-center">
              <DollarSign size={28} className="mx-auto text-black/20 mb-2" />
              <div className="font-bold text-black/55 text-sm">No payouts yet</div>
            </div>
          ) : (
            <div className="divide-y divide-black/5 max-h-[320px] overflow-y-auto font-medium">
              {items.map((p) => (
                <div key={p.id} className="p-4 flex items-center justify-between text-sm hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <DollarSign size={15} className="text-emerald-600 bg-emerald-50 border border-emerald-100 rounded p-0.5 w-6 h-6" />
                    <div>
                      <div className="font-bold text-black font-mono">{ngn(p.amount)}</div>
                      <div className="text-xs text-black/40 font-mono mt-0.5">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</div>
                    </div>
                  </div>
                  <span className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${
                    p.status === "approved" ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
                    p.status === "rejected" ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                    {p.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RefShare({ me }) {
  const [links, setLinks] = useState(null);
  useEffect(() => { rapi().get("/referral/share").then((r) => setLinks(r.data)); }, []);
  
  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied!"); };
  if (!links) return <div className="p-8"><Loader2 className="animate-spin text-emerald-600" /></div>;

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-4xl" data-testid="ref-share">
      <h1 className="font-display font-black text-2xl text-black">Share & Earn</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-stretch">
        <div className="bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 text-white rounded-2xl p-6 md:col-span-5 shadow-sm flex flex-col justify-between">
          <div>
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block">Your unique referral code</span>
            <div className="font-display font-black text-4xl font-mono text-center tracking-wider text-emerald-400 bg-slate-950 border border-slate-800/80 py-4 rounded-xl select-all my-5 shadow-inner">
              {links.code}
            </div>
          </div>
          <button onClick={() => copy(links.code)} className="w-full px-4 py-3 rounded-xl bg-white hover:bg-slate-50 text-slate-950 font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-sm" data-testid="ref-share-copy-code">
            <Copy size={13} /> Copy code
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm md:col-span-7 space-y-4 flex flex-col justify-center">
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-wider font-bold text-black/45 block">Registration link</label>
              <div className="flex items-center gap-1.5">
                <input value={links.registration_url} readOnly className="flex-1 border border-black/10 rounded-xl px-3 py-2 text-xs font-mono bg-slate-50 text-slate-600 focus:outline-none select-all" />
                <button onClick={() => copy(links.registration_url)} className="p-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white border border-slate-900 transition-colors"><Copy size={13} /></button>
              </div>
            </div>
            
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-wider font-bold text-black/45 block">Landing page</label>
              <div className="flex items-center gap-1.5">
                <input value={links.landing_url} readOnly className="flex-1 border border-black/10 rounded-xl px-3 py-2 text-xs font-mono bg-slate-50 text-slate-600 focus:outline-none select-all" />
                <button onClick={() => copy(links.landing_url)} className="p-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white border border-slate-900 transition-colors"><Copy size={13} /></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RefProfile({ me, setMe }) {
  const [f, setF] = useState({
    full_name: me?.full_name || "", phone: me?.phone || "",
    country: me?.country || "", state: me?.state || "", city: me?.city || "",
  });
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await rapi().patch("/referral/me", f);
      toast.success("Profile updated");
      const r = await rapi().get("/referral/me");
      setMe(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const changePwd = async () => {
    if (!pwd.next || pwd.next !== pwd.confirm) return toast.error("Passwords don't match");
    if (pwd.next.length < 8) return toast.error("Password must be at least 8 characters");
    setSaving(true);
    try {
      await rapi().post("/referral/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Password changed");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-4xl" data-testid="ref-profile">
      <h1 className="font-display font-black text-2xl text-black">Profile & settings</h1>

      <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 border-b border-black/5 pb-5">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 text-emerald-400 flex items-center justify-center font-display font-black text-xl shadow-inner border border-slate-800">
            {(me?.full_name || "U")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg text-black truncate">{me?.full_name}</div>
            <div className="text-xs text-black/45 font-mono truncate mt-0.5">{me?.email}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field lbl="Full name"><Input value={f.full_name} onChange={(v) => setF({ ...f, full_name: v })} testid="ref-profile-name" /></Field>
          <Field lbl="Phone"><Input value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="ref-profile-phone" /></Field>
          <Field lbl="Country"><Input value={f.country} onChange={(v) => setF({ ...f, country: v })} /></Field>
          <Field lbl="State"><Input value={f.state} onChange={(v) => setF({ ...f, state: v })} /></Field>
          <Field lbl="City"><Input value={f.city} onChange={(v) => setF({ ...f, city: v })} /></Field>
        </div>
        
        <button onClick={save} disabled={saving} data-testid="ref-profile-save"
          className="px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm disabled:opacity-50 transition-colors shadow-sm">
          {saving ? "Saving…" : "Save profile"}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm space-y-4">
        <div>
          <h3 className="font-display font-bold text-base text-black">Change password</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input type="password" placeholder="Current password" value={pwd.current} onChange={(v) => setPwd({ ...pwd, current: v })} />
          <Input type="password" placeholder="New password" value={pwd.next} onChange={(v) => setPwd({ ...pwd, next: v })} />
          <Input type="password" placeholder="Confirm new password" value={pwd.confirm} onChange={(v) => setPwd({ ...pwd, confirm: v })} />
        </div>
        <button onClick={changePwd} disabled={saving} className="px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm disabled:opacity-50 transition-colors shadow-sm">
          {saving ? "Processing..." : "Change password"}
        </button>
      </div>
    </div>
  );
}

// ──────────────────── HELPERS ────────────────────
function Field({ lbl, children }) {
  return (
    <div className="space-y-1.5 w-full">
      <label className="text-[11px] uppercase tracking-wider font-bold text-black/45 block">{lbl}</label>
      {children}
    </div>
  );
}

function Input({ testid, ...props }) {
  return (
    <input 
      {...props} 
      data-testid={testid} 
      onChange={(e) => props.onChange(e.target.value)}
      className="w-full px-4 py-3 rounded-xl border border-black/10 bg-white text-sm text-slate-900 placeholder-black/30 font-medium focus:border-emerald-600 focus:outline-none focus:ring-4 focus:ring-emerald-600/5 transition-all" 
    />
  );
}